/**
  * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * WelcomeCard
 * Main component to show welcome card with all sections
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import CustomBanner from './CustomBanner';

const WelcomeCardRoot = styled('div')({
  '&&': {
    width: '100%',
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 32,
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'space-between'
  }
});

const WelcomeCardTitle = styled(Typography)({
  '&&': {
    fontSize: mixins.fontSize24,
    fontWeight: mixins.fontWeight700,
    color: mixins.color_text_primary,
    marginBottom: 16,
    lineHeight: '120%'
  }
});

const SectionTitle = styled(Typography)({
  '&&': {
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    color: mixins.color_text_primary,
    lineHeight: '28px',
    fontFamily: mixins.fontFamilyPlusSans,
    letterSpacing: '-0.4px'
  }
});

const InstructionsList = styled('div')({
  '&&': {
    paddingLeft: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  }
});

const InstructionItem = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: mixins.fontSize18,
    color: mixins.color_text_primary,
    lineHeight: '28px',
    gap: 8,
    fontFamily: mixins.fontFamilyPlusSans
  }
});

const InstructionHeader = styled('div')({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    display: 'flex',
    alignItems: 'baseline'
  }
});

const InstructionContent = styled('div')({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  }
});

const InstructionText = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    flex: 1,
    fontSize: mixins.fontSize18,
    color: mixins.color_text_primary,
    lineHeight: '28px'
  }
});

const InstructionTitle = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight600,
    color: mixins.color_text_primary,
    lineHeight: '28px'
  }
});

const InstructionDescription = styled(Typography)({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    color: mixins.zambezi,
    lineHeight: '24px'
  }
});

const CtaButton = styled(Button)({
  '&&': {
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    padding: '12px 24px',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    textTransform: 'none',
    borderRadius: 1000,
    minWidth: 140,
    width: 168,
    height: 52,
    position: 'relative',
    '&:hover': {
      backgroundColor: mixins.amethyst,
      opacity: 0.9,
      color: mixins.chalkWhiteFont
    },
    gap: '8px',
    '& span': {
      width: '100%',
      display: 'inherit'
    }
  }
});

const IllustrationContainer = styled('div')({
  '&&': {
    position: 'absolute',
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    '& img': {
      width: 300
    }
  }
});

const ContentContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  }
});

const CtaButtonContainer = styled('div')({
  '&&': {
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'column'
  }
});



/**
 * WelcomeCard Component
 * Main component that renders the welcome card with all sections
 */
const WelcomeCard = ({
  image,
  header,
  content,
  buttonCta,
  showButton,
  onCtaClick,
  className,
  banner,
  showBanner,
  onInit = () => null
}) => {
  const [isBannerVisible, setIsBannerVisible] = useState(showBanner);

  const handleBannerClose = () => {
    setIsBannerVisible(false);
    banner.onBannerClose();
  };


  useEffect(() => {
    onInit();
  }, []);

  return (
    <WelcomeCardRoot className={className || ''}>
      {header && (
        <WelcomeCardTitle>
          {header}
        </WelcomeCardTitle>
      )}
      {isBannerVisible && (
        <CustomBanner
          type={banner.type}
          text={banner.text}
          onClose={handleBannerClose}
        />
      )}
      {content && (
        <ContentContainer>
          <SectionTitle variant="h5">
            {content.sectionTitle}
          </SectionTitle>

          {content.instructions && (
            <InstructionsList>
              {content.instructions.map((instruction, index) => (
                <InstructionItem key={index}>
                  <InstructionHeader>
                    <InstructionContent>
                      {typeof instruction === 'string' ? (
                        <InstructionText>
                          {instruction}
                        </InstructionText>
                      ) : (
                        <>
                          <InstructionTitle>
                            {instruction.title}
                          </InstructionTitle>
                          <InstructionDescription>
                            {instruction.description}
                          </InstructionDescription>
                        </>
                      )}
                    </InstructionContent>
                  </InstructionHeader>
                </InstructionItem>
              ))}
            </InstructionsList>
          )}
        </ContentContainer>
      )}
      {showButton && buttonCta && (
        <CtaButtonContainer>
          <CtaButton
            onClick={onCtaClick}
            variant="contained"
          >
            {buttonCta.text}
            {buttonCta.icon}
          </CtaButton>
        </CtaButtonContainer>
      )}
      {image && (
        <IllustrationContainer>
          {image}
        </IllustrationContainer>
      )}
    </WelcomeCardRoot>
  );
};

WelcomeCard.propTypes = {
  image: PropTypes.node,
  header: PropTypes.string,
  content: PropTypes.shape({
    sectionTitle: PropTypes.string,
    instructions: PropTypes.arrayOf(PropTypes.string)
  }),
  buttonCta: PropTypes.shape({
    text: PropTypes.string,
    icon: PropTypes.node
  }),
  showButton: PropTypes.bool,
  onCtaClick: PropTypes.func,
  className: PropTypes.string,
  banner: PropTypes.shape({
    type: PropTypes.string,
    text: PropTypes.string,
    onBannerClose: PropTypes.func
  }),
  showBanner: PropTypes.bool,
  onInit: PropTypes.func
};

WelcomeCard.defaultProps = {
  showButton: true,
  content: {
    sectionTitle: '',
    instructions: [
    ]
  },
  buttonCta: {
    text: '',
    icon: null
  },
  onCtaClick: () => {},
  className: '',
  image: null,
  header: null,
  banner: {
    type: 'alert',
    text: '',
    onBannerClose: () => {}
  },
  showBanner: false,
  onInit: () => {}
};

export default WelcomeCard;
